import { AccessibleClickDirective } from '../../../directives/accessible-click.directive';
import { CommonModule } from '@angular/common';
import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { DynamicDialogRef } from '../../../../core/services/dialog';
import { DownloadService } from '../../../../core/services/download/download.service';
import {
  DownloadRecord,
  RecentDownloadsService,
} from '../../../../core/services/recent-downloads/recent-downloads.service';
import { ToastService } from '../../../../core/services/toast/toast.service';
import { UtilitiesService } from '../../../../core/services/utilities/utilities.service';
import { TranslatePipe } from '../../../pipes/translate/translate.pipe';
import { IcDownloadComponent } from '../../Icons/ic-download/ic-download.component';
import { TextButton } from '../../buttons/text-button/text-button';
import { Card } from '../../cards/card/card';
import { DialogHeaderComponent } from '../../dialog/dialog-header/dialog-header.component';

/**
 * RecentDownloadsPopupComponent component.
 * Provides UI presentation and event handling for recent downloads popup component.
 */
@Component({
  selector: 'app-recent-downloads-popup',
  standalone: true,
  imports: [
    AccessibleClickDirective,
    CommonModule,
    TranslatePipe,
    IcDownloadComponent,
    Card,
    DialogHeaderComponent,
    TextButton,
  ],
  templateUrl: './recent-downloads-popup.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./recent-downloads-popup.component.scss'],
})
export class RecentDownloadsPopupComponent {
  /** Injected service for dialog ref. */
  private dialogRef = inject(DynamicDialogRef);
  /** Injected service for recent downloads service. */
  private recentDownloadsService = inject(RecentDownloadsService);
  /** Injected service for utils. */
  private utils = inject(UtilitiesService);
  /** Injected service for download service. */
  private downloadService = inject(DownloadService);
  /** Injected service for toast service. */
  private toastService = inject(ToastService);

  /** Property downloads. */
  downloads = this.recentDownloadsService.downloads;

  /**
   * Handles on open folder logic.
   * @param record - The record
   */
  onOpenFolder(record: DownloadRecord) {
    if (record.apiPath) {
      // Re-fetch using the same authenticated params as the original download.
      this.downloadService
        .downloadDoc(record.apiPath, record.downloadParams as any)
        .subscribe({
          next: (blob: Blob) => {
            this.utils.saveFile(
              blob,
              record.filename,
              record.type,
              record.apiPath,
              record.downloadParams
            );
          },
          error: () => {
            this.toastService.showError('Error', 'oopsSomethingWentWrong');
          },
        });
    } else if (record.url && !record.url.startsWith('blob:')) {
      // Direct CDN / S3 link — just follow it.
      const a = document.createElement('a');
      a.href = record.url;
      a.download = record.filename;
      a.click();
    } else {
      // Record is from an old session and can no longer be fetched.
      this.toastService.showWarning('Warning', 'fileNoLongerAvailable');
    }
  }

  /**
   * Handles format date logic.
   * @param timestamp - The timestamp
   */
  formatDate(timestamp: number): string {
    return this.utils.formatToZonedTime(timestamp, 'dd MMM, HH:mm');
  }

  /**
   * Handles on close logic.
   */
  onClose() {
    this.dialogRef.close();
  }

  /**
   * Handles clear all logic.
   */
  clearAll() {
    this.recentDownloadsService.clearHistory();
  }
}
